import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';

// Basic error logging only
console.log('🚀 SleepCalc Pro initializing...');

// Get root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  console.error('❌ Root element not found!');
  throw new Error('Root element not found');
}

// Simple error boundary component
function ErrorFallback({ error }: { error: Error }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'system-ui, sans-serif',
      backgroundColor: '#f8fafc'
    }}>
      <div style={{
        maxWidth: '500px',
        textAlign: 'center',
        background: 'white',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>
          ⚠️ SleepCalc Pro 加载失败
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '20px' }}>
          应用程序遇到错误: {error.message}
        </p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            margin: '5px'
          }}
        >
          🔄 刷新页面
        </button>
      </div>
    </div>
  );
}

// Minimal Error Boundary wrapper
class MinimalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 React Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

// Mount the app with minimal error handling
try {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <MinimalErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </HelmetProvider>
      </MinimalErrorBoundary>
    </React.StrictMode>
  );
  
  console.log('✅ React app mounted successfully');
  
  // Mark app as loaded and hide fallback UI
  setTimeout(() => {
    document.body.classList.add('app-loaded');
    console.log('✅ App marked as loaded, fallback UI should hide');
  }, 100);
} catch (error) {
  console.error('❌ Failed to mount React app:', error);
  
  // Last resort fallback
  rootElement.innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; font-family: system-ui;">
      <div style="text-align: center; background: white; padding: 30px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        <h2 style="color: #dc2626; margin-bottom: 16px;">⚠️ 严重错误</h2>
        <p style="color: #6b7280; margin-bottom: 20px;">React 应用无法启动</p>
        <button onclick="window.location.reload()" style="padding: 10px 20px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer;">
          🔄 刷新页面
        </button>
      </div>
    </div>
  `;
}